import { AlertTriangle, BadgePercent, TicketPercent, type LucideIcon } from 'lucide-react';

import { StatusBadge } from '@/shared/components/status-badge';
import { formatCurrency } from '@/shared/lib/format';

import type { PricingCoupon, PricingGlobalDiscount } from '../types/pricing.types';
import { formatEngineValue, formatFraction } from './pricing-format';
import { WorkedFormula } from './worked-formula';

/*
 * Display only: the discount, the price paid and the margin after it were calculated by the backend
 * with the same formula checkout uses (COUPONS_PLAN.md §5a, GLOBAL_DISCOUNT_PLAN.md §2).
 */

type Before = {
  netRevenue: number | null;
  grossProfit: number | null;
  realizedMargin: number | null;
} | null;

interface AppliedDiscount {
  listPrice: number | null;
  discountAmount: number | null;
  chargedPrice: number | null;
  percentOff: number;
  maxDiscountAmount: number | null;
  capApplied: boolean;
  belowCost: boolean;
  currency: string | null;
  afterDiscount: Before;
  resultFormula: string[];
}

/** The engine's price, the discount, what the customer pays, and the margin before and after. */
function DiscountResult({
  label,
  heading,
  name,
  icon: Icon,
  discount,
  before,
  testId,
  note,
}: {
  label: string;
  heading: string;
  /** "coupon" or "global discount", for the margin table and warnings. */
  name: string;
  icon: LucideIcon;
  discount: AppliedDiscount;
  before: Before;
  testId: string;
  note?: React.ReactNode;
}) {
  const currency = discount.currency ?? undefined;
  const rows: [string, string, string][] = [
    [
      'Net revenue',
      formatEngineValue(before?.netRevenue, currency),
      formatEngineValue(discount.afterDiscount?.netRevenue, currency),
    ],
    [
      'Gross profit',
      formatEngineValue(before?.grossProfit, currency),
      formatEngineValue(discount.afterDiscount?.grossProfit, currency),
    ],
    [
      'Realized margin',
      formatFraction(before?.realizedMargin),
      formatFraction(discount.afterDiscount?.realizedMargin),
    ],
  ];

  return (
    <section aria-label={label} className="border-border bg-card overflow-hidden rounded-lg border">
      <div className="flex flex-wrap items-start justify-between gap-4 p-4">
        <div className="flex flex-col gap-1">
          <p className="text-muted-foreground flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase">
            <Icon className="size-3.5" aria-hidden />
            {heading}
          </p>
          <p className="text-3xl font-semibold tracking-tight tabular-nums" data-testid={testId}>
            {formatCurrency(discount.chargedPrice, currency, { precise: true })}
          </p>
          <p className="text-muted-foreground text-xs tabular-nums">
            {formatCurrency(discount.listPrice, currency, { precise: true })} −{' '}
            {formatCurrency(discount.discountAmount, currency, { precise: true })} (
            {discount.percentOff}%
            {discount.maxDiscountAmount !== null &&
              `, max ${formatCurrency(discount.maxDiscountAmount, currency, { precise: true })}`}
            )
          </p>
          {note}
        </div>
        <div className="flex flex-col items-end gap-1">
          {discount.capApplied && <StatusBadge variant="info">Cap applied</StatusBadge>}
          {discount.belowCost && <StatusBadge variant="danger">Below cost</StatusBadge>}
        </div>
      </div>
      {discount.belowCost && (
        <p className="border-status-danger-border bg-status-danger-bg text-status-danger flex items-start gap-2 border-t px-4 py-2 text-xs">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          With this {name} the item is sold below what it costs. It is allowed — this is a warning
          only.
        </p>
      )}
      <table
        className="border-border w-full border-t text-xs"
        aria-label={`Margin before and after the ${name}`}
      >
        <thead className="bg-muted/30 text-muted-foreground">
          <tr>
            <th className="px-4 py-1.5 text-start font-medium" scope="col">
              &nbsp;
            </th>
            <th className="px-4 py-1.5 text-end font-medium" scope="col">
              Without {name}
            </th>
            <th className="px-4 py-1.5 text-end font-medium" scope="col">
              With {name}
            </th>
          </tr>
        </thead>
        <tbody className="divide-border divide-y">
          {rows.map(([row, without, withDiscount]) => (
            <tr key={row}>
              <th className="text-muted-foreground px-4 py-1.5 text-start font-normal" scope="row">
                {row}
              </th>
              <td className="px-4 py-1.5 text-end tabular-nums">{without}</td>
              <td className="px-4 py-1.5 text-end font-medium tabular-nums">{withDiscount}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {discount.resultFormula.length > 0 && (
        <div className="border-border border-t px-4 py-3 text-xs">
          <WorkedFormula
            lines={discount.resultFormula}
            label={`Revenue and margin after the ${name}`}
          />
        </div>
      )}
    </section>
  );
}

/**
 * What a coupon does to an item's price. Used by the pricing calculator and an order's pricing view.
 */
export function CouponResult({ coupon, before }: { coupon: PricingCoupon; before: Before }) {
  const label = coupon.code ? `Coupon ${coupon.code}` : 'Coupon';

  if (!coupon.applies)
    return (
      <section
        aria-label={label}
        className="border-border bg-card flex items-start gap-2 rounded-lg border p-3 text-xs"
      >
        <TicketPercent className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
        <div>
          <p className="font-medium">
            {label} does not apply to this item
            {coupon.notAppliedReason && (
              <span className="text-muted-foreground font-mono"> ({coupon.notAppliedReason})</span>
            )}
          </p>
          <p className="text-muted-foreground">
            {coupon.notAppliedMessage ?? 'Checkout would charge the final price above.'}
          </p>
        </div>
      </section>
    );

  return (
    <DiscountResult
      label={label}
      heading={`Customer pays with ${coupon.code ?? 'the coupon'}`}
      name="coupon"
      icon={TicketPercent}
      discount={coupon}
      before={before}
      testId="coupon-price"
    />
  );
}

const SALE_STATUS: Record<string, string> = {
  live: 'Running now',
  scheduled: 'Scheduled — preview',
  ended: 'Ended — preview',
  off: 'Switched off — preview',
};

/**
 * What the country's global discount does to an item's price. In the calculator it is the live sale
 * (exactly as checkout applies it) or a previewed one; on an order, the one the order was paid with.
 */
export function GlobalDiscountResult({
  discount,
  before,
}: {
  discount: PricingGlobalDiscount;
  before: Before;
}) {
  return (
    <DiscountResult
      label="Global discount"
      heading="Customer pays with the global discount"
      name="global discount"
      icon={BadgePercent}
      discount={{ ...discount, afterDiscount: discount.afterDiscount ?? null }}
      before={before}
      testId="global-discount-price"
      note={
        discount.status ? (
          <p className="text-muted-foreground text-xs">
            {SALE_STATUS[discount.status] ?? discount.status}
          </p>
        ) : null
      }
    />
  );
}
